import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { Plus, Pencil, Trash2, Loader2, Table2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddBookingModal from "@/components/AddBookingModal";
import {
  getTransactions, getStaff, getServices, deleteTransaction, getAttendance,
} from "@/lib/api";

const ALL = "__all__";

// Fixed 5-color pastel palette. Therapists are mapped by sorted index within
// the active date (palette[index % 5]) so colors are stable across re-renders.
import { NEUTRAL_BORDER, softTint, PILL, buildTherapistColorMap } from "@/lib/therapistColors";

// Payment text colors: Cash green, Eftpos/Card sky blue, other/split slate.
const paymentColor = (method) => {
  const m = (method || "").toLowerCase();
  if (m === "cash") return "#16A34A";
  if (m === "eftpos" || m === "card") return "#0284C7";
  return "#475569";
};

// 24h "HH:MM" -> 12-hour clock, e.g. "9:30 AM".
const to12h = (t) => {
  if (!t || !t.includes(":")) return t || "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${period}`;
};

export default function DataTable() {
  const { selectedBranch, branches, selectedDate } = useOutletContext();
  const branchId = selectedBranch || branches[0]?.id;
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const [rows, setRows] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  const [filters, setFilters] = useState({
    therapist: ALL, service: ALL, status: ALL, payment: ALL,
  });

  const fetchData = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [tx, staff, svc, att] = await Promise.all([
        getTransactions({ date: dateStr, branch_id: branchId }),
        getStaff(),
        getServices(),
        getAttendance(dateStr, branchId),
      ]);
      tx.sort((a, b) => (a.time_start || "").localeCompare(b.time_start || ""));
      setRows(tx);
      setAllStaff(staff);
      setServices(svc);
      setAttendance(Array.isArray(att) ? att : []);
    } catch {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [branchId, dateStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const therapistOptions = useMemo(
    () => [...new Set(rows.map((r) => r.staff_name).filter(Boolean))].sort(),
    [rows]
  );
  const serviceOptions = useMemo(
    () => [...new Set(rows.map((r) => r.service_name).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filters.therapist !== ALL && r.staff_name !== filters.therapist) return false;
      if (filters.service !== ALL && r.service_name !== filters.service) return false;
      if (filters.status !== ALL && r.booking_status !== filters.status) return false;
      if (filters.payment !== ALL && r.payment_method !== filters.payment) return false;
      return true;
    });
  }, [rows, filters]);

  // Dynamic daily mapping: unique therapists on this date, sorted, mapped
  // sequentially to palette[index % 5] — stable across re-renders.
  const therapistColorMap = useMemo(() => {
    const names = [];
    rows.forEach((r) => {
      (r.therapist_names && r.therapist_names.length ? r.therapist_names : [r.staff_name]).forEach((n) => names.push(n));
    });
    return buildTherapistColorMap(names);
  }, [rows]);
  const colorFor = (name) => therapistColorMap[name] || NEUTRAL_BORDER;

  // Breakdown ordered by arrival (clock-in) sequence; not-checked-in sort last alphabetically.
  const therapistBreakdown = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      (r.therapist_ids && r.therapist_ids.length ? r.therapist_ids : [r.staff_id]).forEach((id, i) => {
        const name = (r.therapist_names && r.therapist_names[i]) || r.staff_name;
        counts[name] = (counts[name] || 0) + 1;
      });
    });
    const clockByName = {};
    attendance.forEach((a) => { clockByName[a.staff_name] = a.clock_in || null; });
    const entries = Object.entries(counts).map(([name, n]) => ({ name, n, clock_in: clockByName[name] || null }));
    entries.sort((a, b) => {
      if (a.clock_in && b.clock_in) return a.clock_in.localeCompare(b.clock_in);
      if (a.clock_in) return -1;
      if (b.clock_in) return 1;
      return a.name.localeCompare(b.name);
    });
    let q = 0;
    return entries.map((e) => ({ ...e, queue: e.clock_in ? ++q : null }));
  }, [rows, attendance]);

  const handleDelete = async () => {
    try {
      await deleteTransaction(deleteRow.id);
      toast.success("Session deleted");
      setDeleteRow(null);
      fetchData();
    } catch {
      toast.error("Failed to delete session");
    }
  };

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const TH = "px-3 py-2 text-left text-xs font-semibold text-sage uppercase tracking-wide";
  const TD = "px-3 py-2 text-sm text-forest whitespace-nowrap";

  return (
    <div className="animate-in space-y-4" data-testid="data-table-page">
      {/* Summary counters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-forest text-white rounded-xl p-4 flex flex-col justify-center" data-testid="total-sessions-card">
          <p className="text-sm opacity-80">Total Sessions ({format(selectedDate, "d MMM")})</p>
          <p className="text-4xl font-bold">{rows.length}</p>
        </div>
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-4">
          <p className="text-sm font-semibold text-sage mb-2">Therapist Breakdown</p>
          <div className="flex flex-wrap gap-2" data-testid="therapist-breakdown">
            {therapistBreakdown.length === 0 ? (
              <span className="text-sm text-sage/60">No sessions yet.</span>
            ) : therapistBreakdown.map((t) => (
              <span key={t.name} className="inline-flex items-center gap-1.5 bg-sand rounded-full px-3 py-1 text-sm" data-testid={`breakdown-${t.name}`}>
                {t.queue && (
                  <span className="w-5 h-5 rounded-full bg-ochre text-white text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`breakdown-queue-${t.name}`}>
                    {t.queue}
                  </span>
                )}
                <b className="text-forest">{t.name}</b>
                <span className="text-sage">{t.n} {t.n === 1 ? "session" : "sessions"}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Table2 className="h-5 w-5 text-forest" />
          <h2 className="font-heading text-xl font-bold text-forest">Data Ledger</h2>
          <span className="text-sm text-sage">· {filtered.length} of {rows.length} shown</span>
        </div>
        <Button className="bg-forest hover:bg-forest-light" onClick={() => setAddOpen(true)} data-testid="add-session-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Session
        </Button>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center h-60"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
        ) : (
          <table className="w-full min-w-[1000px]">
            <thead className="bg-sand border-b border-border">
              <tr>
                <th className={TH}>No.</th>
                <th className={TH}>Client</th>
                <th className={TH}>Status</th>
                <th className={TH}>Service</th>
                <th className={TH}>Duration</th>
                <th className={TH}>Payment</th>
                <th className={TH}>Price ($)</th>
                <th className={TH}>Therapist</th>
                <th className={TH}>Start</th>
                <th className={TH}>End</th>
                <th className={TH}>Action</th>
              </tr>
              {/* Filter row: only Therapist, Service, Status, Payment */}
              <tr className="border-b border-border bg-white">
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5">
                  <FilterSelect value={filters.status} onChange={(v) => setF("status", v)} options={["Walk-in", "Book"]} testid="filter-status" />
                </th>
                <th className="px-2 py-1.5">
                  <FilterSelect value={filters.service} onChange={(v) => setF("service", v)} options={serviceOptions} testid="filter-service" />
                </th>
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5">
                  <FilterSelect value={filters.payment} onChange={(v) => setF("payment", v)} options={["Cash", "Eftpos", "Split"]} testid="filter-payment" />
                </th>
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5">
                  <FilterSelect value={filters.therapist} onChange={(v) => setF("therapist", v)} options={therapistOptions} testid="filter-therapist" />
                </th>
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-10 text-sage">No sessions match your filters.</td></tr>
              ) : filtered.map((r, idx) => {
                const isDual = (r.therapist_ids || []).length > 1;
                const names = (r.therapist_names && r.therapist_names.length) ? r.therapist_names : [r.staff_name];
                const uniqNames = [...new Set(names.filter(Boolean))];
                const rowBg = uniqNames.length === 1 ? softTint(colorFor(uniqNames[0]), 0.4) : "#FFFFFF";
                const rowBorder = uniqNames.length === 1 ? `4px solid ${colorFor(uniqNames[0])}` : `4px solid ${NEUTRAL_BORDER}`;
                return (
                  <tr
                    key={r.id}
                    className="border-b border-border/60 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: rowBg, borderLeft: rowBorder }}
                    data-testid={`session-row-${r.id}`}
                  >
                    <td className={`${TD} text-sage`}>{idx + 1}</td>
                    <td className={`${TD} font-medium`}>{r.client_name}</td>
                    <td className={TD}>
                      <span
                        style={{ ...PILL, padding: "2px 10px", fontWeight: 600, color: r.booking_status === "Book" ? "#D97706" : "#0369A1" }}
                        className="text-xs"
                      >
                        {r.booking_status === "Book" ? "Booked" : "Walk-in"}
                      </span>
                    </td>
                    <td className={TD}>{r.service_name}</td>
                    <td className={TD}>{r.duration} min</td>
                    <td className={TD}>
                      <span
                        style={{ ...PILL, padding: "2px 10px", fontWeight: 600, color: paymentColor(r.payment_method) }}
                        className="text-xs"
                      >
                        {r.payment_method}
                      </span>
                    </td>
                    <td className={`${TD} font-medium`}>${Number(r.price || 0).toFixed(2)}</td>
                    <td className={`${TD} font-medium`}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {names.map((nm, i) => (
                          <span
                            key={i}
                            style={{ ...PILL, padding: "3px 10px", gap: "6px", color: "#1E293B", fontWeight: 600 }}
                            className="text-xs"
                            data-testid={`therapist-tag-${r.id}-${i}`}
                          >
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colorFor(nm) }} />
                            {nm}
                          </span>
                        ))}
                        {isDual && <span className="text-[10px] text-ochre font-semibold">4-Hand</span>}
                      </div>
                    </td>
                    <td className={TD}>{to12h(r.time_start)}</td>
                    <td className={TD}>{to12h(r.time_end)}</td>
                    <td className={TD}>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditRow(r)} data-testid={`edit-session-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5 text-forest" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDeleteRow(r)} data-testid={`delete-session-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit modal (reuses New Booking modal) */}
      <AddBookingModal
        open={addOpen}
        onOpenChange={setAddOpen}
        branchId={branchId}
        date={selectedDate}
        staff={allStaff}
        services={services}
        onCreated={() => { setAddOpen(false); fetchData(); }}
      />
      <AddBookingModal
        open={!!editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        branchId={branchId}
        date={selectedDate}
        staff={allStaff}
        services={services}
        editTransaction={editRow}
        onCreated={() => { setEditRow(null); fetchData(); }}
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(v) => { if (!v) setDeleteRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow && `${deleteRow.client_name} · ${deleteRow.service_name} · ${deleteRow.time_start}`} — this cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-session">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs" data-testid={testid}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o === "Book" ? "Booked" : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
